import {
  ANSWER_KEYS, DATA_TYPES, DEMANDS, INCIDENT_TYPES, MAGNITUDES, ORG_TYPES, SYSTEMS, US_STATES, emptyIntake,
  type AnswerKey, type Intake, type YesNoUnknown,
} from "./intake"

/**
 * Intake draft: the answers typed so far, saved in this browser so refresh / Back / a bookmarked
 * "?q=" step all resume where the person was. Nothing here is sent anywhere. The optional share
 * link carries the same answers in the URL FRAGMENT (#a=...), which browsers never send to a server.
 */
export const DRAFT_KEY = "aab.draft.v1"

export interface Draft {
  intake: Intake
  /** datetime-local strings as typed in the wizard (local time). */
  noticed: string
  confirmed: string
}

const YNU: YesNoUnknown[] = ["yes", "no", "unknown"]
const ynu = (v: unknown, fallback: YesNoUnknown = "unknown"): YesNoUnknown => (YNU.includes(v as YesNoUnknown) ? (v as YesNoUnknown) : fallback)
const pickIds = <T extends string>(v: unknown, allowed: readonly { id: T }[] | readonly string[]): T[] => {
  const ids = new Set((allowed as readonly (string | { id: string })[]).map((x) => (typeof x === "string" ? x : x.id)))
  return Array.isArray(v) ? (v.filter((x) => typeof x === "string" && ids.has(x)) as T[]) : []
}
const isoOrEmpty = (v: unknown): string => {
  if (typeof v !== "string" || !v) return ""
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? "" : v
}
const localOrEmpty = (v: unknown): string => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v) ? v : "")

/** Accepts anything (a stored draft, a decoded link) and returns a valid Intake. */
export function sanitizeIntake(raw: unknown): Intake {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>
  const base = emptyIntake()
  const type = INCIDENT_TYPES.find((t) => t.id === r.incidentType)?.id ?? null
  return {
    ...base,
    incidentType: type,
    stillActive: ynu(r.stillActive),
    unauthorizedAccess: ynu(r.unauthorizedAccess),
    dataTaken: ynu(r.dataTaken),
    noticedAt: isoOrEmpty(r.noticedAt),
    confirmedAt: isoOrEmpty(r.confirmedAt),
    orgTypes: pickIds(r.orgTypes, ORG_TYPES),
    affectedInUS: ynu(r.affectedInUS),
    affectedStates: pickIds(r.affectedStates, US_STATES.map((s) => s.code)),
    affectedOutsideUS: ynu(r.affectedOutsideUS),
    dataTypes: pickIds(r.dataTypes, DATA_TYPES),
    encrypted: ynu(r.encrypted),
    magnitude: MAGNITUDES.find((m) => m.id === r.magnitude)?.id ?? "unknown",
    systems: pickIds(r.systems, SYSTEMS),
    backupsIntact: ynu(r.backupsIntact),
    hasInsurance: ynu(r.hasInsurance),
    hasCounsel: ynu(r.hasCounsel),
    hasIrProvider: ynu(r.hasIrProvider),
    demand: DEMANDS.find((d) => d.id === r.demand)?.id ?? "unknown",
    paid: ynu(r.paid),
    touched: pickIds<AnswerKey>(r.touched, ANSWER_KEYS),
  }
}

export function sanitizeDraft(raw: unknown): Draft | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  return { intake: sanitizeIntake(r.intake), noticed: localOrEmpty(r.noticed), confirmed: localOrEmpty(r.confirmed) }
}

// ---- share link (fragment) ----------------------------------------------------------------
const b64urlEncode = (s: string) => {
  const bytes = new TextEncoder().encode(s)
  let bin = ""
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
}
const b64urlDecode = (s: string) => {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4))
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad)
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))
}

export function encodeShare(d: Draft): string {
  return "v1." + b64urlEncode(JSON.stringify({ i: d.intake, n: d.noticed, c: d.confirmed }))
}

/** Returns null for anything malformed or from an unknown version. Content is always re-validated. */
export function decodeShare(s: string | null | undefined): Draft | null {
  if (!s || !s.startsWith("v1.") || s.length > 4000) return null
  try {
    const j = JSON.parse(b64urlDecode(s.slice(3))) as { i?: unknown; n?: unknown; c?: unknown }
    return sanitizeDraft({ intake: j.i, noticed: j.n, confirmed: j.c })
  } catch {
    return null
  }
}

/** Reads "#a=<payload>" from a URL hash. */
export function shareFromHash(hash: string): string | null {
  const m = /^#?a=([A-Za-z0-9._-]+)$/.exec(hash)
  return m ? m[1] : null
}

// ---- URL position ---------------------------------------------------------------------------
/** Index of the screen named in ?q=, or 0 when missing or unknown. */
export function screenIndex(param: string | null, screens: readonly string[]): number {
  const i = param ? screens.indexOf(param) : -1
  return i >= 0 ? i : 0
}

// ---- storage ------------------------------------------------------------------------------
export function loadDraft(): Draft | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY)
    return raw ? sanitizeDraft(JSON.parse(raw)) : null
  } catch {
    return null
  }
}
export function saveDraft(d: Draft): void {
  try { window.localStorage.setItem(DRAFT_KEY, JSON.stringify(d)) } catch { /* ignore */ }
}
export function clearDraft(): void {
  try { window.localStorage.removeItem(DRAFT_KEY) } catch { /* ignore */ }
}
